import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog.jsx";
import { Button } from "../../components/ui/button.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Textarea } from "../../components/ui/textarea.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar.jsx";
import { cn } from "../../lib/utils.js";
import { Copy, Link2, Mail, MessageCircle, Share2 } from "lucide-react";

const CHANNEL_OPTIONS = [
  { id: "email", label: "Email", icon: Mail },
  { id: "sms", label: "SMS", icon: MessageCircle },
  { id: "chat", label: "In-app chat", icon: Share2 },
];

const SHARE_TARGETS = [
  { id: "zoom", label: "Zoom" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "teams", label: "Microsoft Teams" },
  { id: "line", label: "LINE" },
];

const toKey = (entry) => entry?.id || entry?.email || entry?.phone || entry?.name || "contact";

export default function VoiceLinkDialog({
  open,
  mode = "send",
  onOpenChange,
  callLink,
  directory = [],
  defaultChannel = "email",
  onComplete,
}) {
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [manualTargets, setManualTargets] = useState("");
  const [notes, setNotes] = useState("");
  const [channel, setChannel] = useState(defaultChannel);
  const [shareTargets, setShareTargets] = useState([]);

  useEffect(() => {
    if (!open) {
      setSelectedContacts([]);
      setManualTargets("");
      setNotes("");
      setChannel(defaultChannel);
      setShareTargets([]);
    }
  }, [open, defaultChannel]);

  const heading = mode === "send" ? "Send call link" : "Share call link";
  const description = mode === "send"
    ? "Choose recipients and a delivery channel to send the call link instantly."
    : "Select integrations and channels to broadcast the call link.";

  const actionableContacts = useMemo(() => directory.map((entry) => ({
    id: toKey(entry),
    name: entry.name || entry.email || entry.phone,
    email: entry.email,
    phone: entry.phone,
  })), [directory]);

  const toggleContact = (id) => {
    setSelectedContacts((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const toggleShareTarget = (id) => {
    setShareTargets((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(callLink);
    } catch {
      window.prompt("Copy call link", callLink);
    }
  };

  const handleSubmit = () => {
    const manualEntries = manualTargets.split(/[\n,;]+/).map((value) => value.trim()).filter(Boolean);
    const resolvedContacts = actionableContacts.filter((entry) => selectedContacts.includes(entry.id));
    const payload = {
      mode,
      channel,
      contacts: resolvedContacts,
      manual: manualEntries,
      notes: notes.trim() || undefined,
      shareTargets,
    };
    onComplete?.(payload);
  };

  const canSubmit = mode === "send"
    ? (selectedContacts.length > 0 || manualTargets.trim().length > 0)
    : shareTargets.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold leading-tight">{heading}</DialogTitle>
          <p className="text-xs text-muted-foreground">{description}</p>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div className="space-y-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Call link</label>
            <div className="rounded-2xl border border-border bg-muted/30 p-4 text-xs">
              <div className="flex items-center justify-between gap-3 break-all">
                <span className="font-medium text-foreground">{callLink}</span>
                <Button type="button" size="sm" variant="ghost" onClick={handleCopy}>
                  <Copy className="mr-2 h-3.5 w-3.5" /> Copy
                </Button>
              </div>
            </div>

            {mode === "send" && (
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Delivery channel</label>
                <div className="grid gap-2 sm:grid-cols-3">
                  {CHANNEL_OPTIONS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setChannel(id)}
                      className={cn(
                        "flex items-center gap-2 rounded-2xl border px-3 py-2 text-left text-xs transition",
                        channel === id ? "border-primary bg-primary/10 text-primary" : "border-border bg-muted/30 hover:border-primary/40",
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message</label>
              <Textarea
                rows={4}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={mode === "send" ? "Add context or agenda" : "Describe how this link will be used"}
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Manual recipients</label>
              <Textarea
                rows={mode === "send" ? 3 : 2}
                value={manualTargets}
                onChange={(event) => setManualTargets(event.target.value)}
                placeholder={mode === "send" ? "Add emails or phone numbers, separated by commas" : "Add channels like #growth, product@company.com"}
              />
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-border bg-muted/20 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Link2 className="h-3.5 w-3.5" /> {mode === "send" ? "Select recipients" : "Share to integrations"}
            </div>
            {mode === "send" ? (
              <div className="space-y-2 max-h-72 overflow-auto pr-1">
                {actionableContacts.length ? (
                  actionableContacts.map((entry) => (
                    <label
                      key={entry.id}
                      className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-transparent px-3 py-2 text-sm transition hover:border-primary/40 hover:bg-primary/5"
                    >
                      <span className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={`https://i.pravatar.cc/80?u=${encodeURIComponent(entry.email || entry.name || entry.id)}`} alt={entry.name} referrerPolicy="no-referrer" />
                          <AvatarFallback>{(entry.name || "C").slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <span>
                          <span className="block font-semibold text-foreground">{entry.name}</span>
                          <span className="text-[11px] text-muted-foreground">{[entry.email, entry.phone].filter(Boolean).join(" • ")}</span>
                        </span>
                      </span>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        checked={selectedContacts.includes(entry.id)}
                        onChange={() => toggleContact(entry.id)}
                      />
                    </label>
                  ))
                ) : (
                  <div className="rounded-2xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                    Connect your directory to see quick recipients.
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {SHARE_TARGETS.map((target) => (
                  <label
                    key={target.id}
                    className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-transparent px-3 py-2 text-sm transition hover:border-primary/40 hover:bg-primary/5"
                  >
                    <span className="font-semibold text-foreground">{target.label}</span>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      checked={shareTargets.includes(target.id)}
                      onChange={() => toggleShareTarget(target.id)}
                    />
                  </label>
                ))}
              </div>
            )}

            {mode === "share" && (
              <div className="rounded-2xl border border-border bg-background/40 p-3 text-xs text-muted-foreground">
                Tip: Add automations from the Connect page to push meeting info into your CRM or ticketing stack.
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {mode === "send" ? "Send link" : "Share link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
